import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Content from '@components/content';
import alertBannerDataJson from '@data/alert-banner.json';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  banner_wrapper,
  banner,
  banner_outage,
  banner_event,
  banner_content,
  banner_close,
  banner_dismissing
} = styles;

// ---------------------------------------------------------

const STORAGE_KEY = 'dismissed-banners';

type Targeting =
  | 'Sitewide'
  | 'Sitewide - exclude Listing pages'
  | 'Sitewide - exclude Results pages'
  | 'Sitewide - exclude Listing and Results pages'
  | 'Only Results page'
  | 'Only Listing pages'
  | 'Only Account pages'
  | 'Results and Listing pages';

type CoreServicesTargeting = 'Mortgage' | 'Insurance' | 'Relocation' | 'Title' | 'Home Services';

type AlertBannerEntry = {
  startDateTime?: string;
  endDateTime?: string;
  targeting?: Targeting;
  sys: { id: string };
  theme: string;
  messageText?: { json: object };
  coreServicesTargeting?: CoreServicesTargeting[];
};

const rawAlertBannerData = alertBannerDataJson as AlertBannerEntry[];

const isAlertBannerEntry = (value: unknown): value is AlertBannerEntry => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as {
    startDateTime?: unknown;
    endDateTime?: unknown;
    targeting?: unknown;
    sys?: { id?: unknown };
    coreServicesTargeting?: CoreServicesTargeting[];
  };

  if (typeof candidate.startDateTime !== 'string') return false;
  if (typeof candidate.endDateTime !== 'string') return false;
  if (typeof candidate.targeting !== 'string' && !Array.isArray(candidate.coreServicesTargeting))
    return false;
  if (!candidate.sys || typeof candidate.sys.id !== 'string') return false;

  return true;
};

const alertBannerData: AlertBannerEntry[] = Array.isArray(rawAlertBannerData)
  ? rawAlertBannerData.filter(isAlertBannerEntry)
  : [];

const matchesTargeting = (targeting: Targeting, pathname: string): boolean => {
  if (!targeting) return false;
  switch (targeting) {
    case 'Sitewide':
      return true;
    case 'Sitewide - exclude Listing pages':
      return !pathname.startsWith('/listing/');
    case 'Sitewide - exclude Results pages':
      return pathname !== '/results';
    case 'Sitewide - exclude Listing and Results pages':
      return (
        !pathname.startsWith('/listing/') && pathname !== '/results'
      );
    case 'Only Results page':
      return pathname === '/results';
    case 'Only Listing pages':
      return pathname.startsWith('/listing/');
    case 'Only Account pages':
      return pathname.startsWith('/account');
    case 'Results and Listing pages':
      return (
        pathname === '/results' || pathname.startsWith('/listing/')
      );
    default:
      return false;
  }
};

const matchesCoreServicesTargeting = (targeting: string[], pathname: string): boolean => {
  if (!targeting || targeting.length === 0) return false;
  if (targeting.find((t) => t === 'Mortgage') && `${pathname}/`.startsWith('/mortgage/'))
    return true;
  if (targeting.find((t) => t === 'Insurance') && `${pathname}/`.startsWith('/insurance/'))
    return true;
  if (targeting.find((t) => t === 'Relocation') && `${pathname}/`.startsWith('/relocation/'))
    return true;
  if (targeting.find((t) => t === 'Title') && `${pathname}/`.startsWith('/title/')) return true;
  if (targeting.find((t) => t === 'Home Services') && `${pathname}/`.startsWith('/home-services/'))
    return true;
  return false;
};

// ---------------------------------------------------------

type AlertBannerDisplayProps = {
  banners: Array<{
    id: string;
    theme: string;
    messageText?: { json: object };
  }>;
  onDismiss: (id: string) => void;
};

export const AlertBannerDisplay: React.FC<AlertBannerDisplayProps> = ({ banners, onDismiss }) => {
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());

  if (banners.length === 0) return null;

  const handleDismissClick = (id: string) => {
    setDismissing((prev) => new Set(prev).add(id));
  };

  const handleTransitionEnd = (id: string) => {
    if (dismissing.has(id)) {
      onDismiss(id);
    }
  };

  return (
    <div className={banner_wrapper}>
      {banners.map((item) => {
        const themeClass = item.theme === 'Outage' ? banner_outage : banner_event;
        const isDismissing = dismissing.has(item.id);
        return (
          <div
            key={item.id}
            className={`${banner} ${themeClass} ${isDismissing ? banner_dismissing : ''}`}
            role="alert"
            onTransitionEnd={() => handleTransitionEnd(item.id)}
          >
            <div className={banner_content}>
              <Content content={item.messageText} />
            </div>
            <button
              className={banner_close}
              onClick={() => handleDismissClick(item.id)}
              aria-label="Dismiss alert"
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------

const AlertBanner: React.FC = () => {
  const router = useRouter();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (Array.isArray(parsed)) {
        const stored = parsed.filter((item): item is string => typeof item === 'string');
        setDismissed(new Set(stored));
      } else {
        setDismissed(new Set());
      }
    } catch {
      setDismissed(new Set());
    }
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const now = Date.now();
  const pathname = router.pathname;
  const path = router.asPath.split('?')[0];

  const activeBanners = alertBannerData.filter((item) => {
    if (!item.startDateTime || !item.endDateTime) return false;
    const start = new Date(item.startDateTime).getTime();
    const end = new Date(item.endDateTime).getTime();
    if (now < start || now > end) return false;
    if (
      !matchesTargeting(item.targeting, pathname) &&
      !matchesCoreServicesTargeting(item.coreServicesTargeting || [], path)
    )
      return false;
    if (dismissed.has(item.sys.id)) return false;
    return true;
  });

  const handleDismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      // localStorage unavailable
    }
  };

  const banners = activeBanners.map((item) => ({
    id: item.sys.id,
    theme: item.theme,
    messageText: item.messageText
  }));

  return <AlertBannerDisplay banners={banners} onDismiss={handleDismiss} />;
};

export default AlertBanner;
